<template>
  <v-dialog v-model="dialog" fullscreen>
    <v-card>
      <!-- HEADER -->
      <v-toolbar color="primary" dark>
        <v-toolbar-title>Audit Log</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="dialog = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-card-text class="pa-4">
        <!-- FILTER -->
        <div class="d-flex align-center flex-wrap gap-4 mb-4 audit-filter">
          <!-- FROM DATE -->
          <v-text-field v-model="fromDateDisplay" label="From Date" placeholder="MM/DD/YYYY" variant="outlined"
            density="compact" class="date-field mr-2" maxlength="10"
            @input="(e) => formatDateInput(e, 'fromDateDisplay')"
            @blur="validateAndFormatDate('fromDateDisplay', 'fromDate')">
            <template #append-inner>
              <v-menu v-model="showFromPicker" :close-on-content-click="false">
                <template #activator="{ props }">
                  <v-icon v-bind="props" @mousedown.prevent>
                    mdi-calendar
                  </v-icon>
                </template>
                <v-date-picker v-model="tempFromDate" @update:model-value="
                  (val) =>
                    setDateFromPicker(
                      val,
                      'fromDateDisplay',
                      'fromDate',
                      'showFromPicker'
                    )
                " hide-header />
              </v-menu>
            </template>
          </v-text-field>

          <!-- TO DATE -->
          <v-text-field v-model="toDateDisplay" label="To Date" placeholder="MM/DD/YYYY" variant="outlined"
            density="compact" class="date-field" maxlength="10" @input="(e) => formatDateInput(e, 'toDateDisplay')"
            @blur="validateAndFormatDate('toDateDisplay', 'toDate')">
            <template #append-inner>
              <v-menu v-model="showToPicker" :close-on-content-click="false">
                <template #activator="{ props }">
                  <v-icon v-bind="props" @mousedown.prevent>
                    mdi-calendar
                  </v-icon>
                </template>
                <v-date-picker v-model="tempToDate" @update:model-value="
                  (val) =>
                    setDateFromPicker(
                      val,
                      'toDateDisplay',
                      'toDate',
                      'showToPicker'
                    )
                " hide-header />
              </v-menu>
            </template>
          </v-text-field>
          <!-- 🔥 ACTION FILTER -->
          <v-autocomplete v-model="selectedAction" :items="actionOptions" label="Action" variant="outlined"
            density="compact" class="mr-2 ml-2 mt-n5" style="width: 180px" item-title="label" item-value="value"
            clearable hide-details />
          <!-- APPLY -->
          <v-btn color="primary" class="apply-btn ml-3 mt-n3" @click="loadAuditLogs">
            APPLY
          </v-btn>
        </div>

        <!-- TABLE -->
        <v-data-table :headers="headers" :items="auditLogsList" :height="tableHeight" :items-per-page="10"
          @update:options="onTableOptionsChange" :loading="loading" density="compact"
          class="elevation-0 custom-data-table">
          <!-- ACTION -->
          <template v-slot:[`item.log_action`]="{ item }">
            <v-chip size="small" color="primary" variant="tonal">
              {{ item.log_action }}
            </v-chip>
          </template>
          <template v-slot:[`item.user_email_id`]="{ item }">
            <div>
              <div class="font-weight-medium">
                {{ item.user_name || "-" }}
              </div>
              <div class="text-caption text-grey">
                {{ item.user_email_id || "-" }}
              </div>
            </div>
          </template>
          <!-- DATE -->
          <template v-slot:[`item.created_on`]="{ item }">
            <div>
              <div class="font-weight-medium">
                {{ formatDate(item.created_on) }}
              </div>
              <div class="text-caption text-grey">
                {{ formatTime(item.created_on) }}
              </div>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import GetAuditLogs from "@/mixins/Organization/GetAuditLogs";

export default {
  name: "AuditLogDialog",
  mixins: [GetAuditLogs],

  props: {
    modelValue: Boolean,
    organizationId: String,
  },

  emits: ["update:modelValue"],

  data() {
    return {
      loading: false,

      fromDate: "",
      toDate: "",

      fromDateDisplay: "",
      toDateDisplay: "",

      tempFromDate: null,
      tempToDate: null,
      selectedAction: null,
      windowHeight: window.innerHeight,
      showFromPicker: false,
      showToPicker: false,

      headers: [
        { title: "User", key: "user_email_id" },
        { title: "Action", key: "log_action" },
        { title: "Description", key: "log_description" },
        { title: "Date", key: "created_on" },
      ],
    };
  },

  computed: {
    tableHeight() {
      return this.windowHeight - 250;
    },
    actionOptions() {
      return [
        { label: "All", value: null },
        { label: "CREATE", value: "CREATE" },
        { label: "UPDATE", value: "UPDATE" },
        { label: "DELETE", value: "DELETE" },
        { label: "DEACTIVATE", value: "DEACTIVATE" },
      ];
    },
    dialog: {
      get() {
        return this.modelValue;
      },
      set(v) {
        this.$emit("update:modelValue", v);
      },
    },
  },

  watch: {
    dialog(val) {
      if (val) {
        this.setDefaultDates();
        this.loadAuditLogs();
      }
    },
  },

  methods: {
    async onTableOptionsChange() {
      const fromTimestamp = this.fromDate
        ? new Date(this.fromDate + "T00:00:00").getTime()
        : undefined;

      const toTimestamp = this.toDate
        ? new Date(this.toDate + "T23:59:59").getTime()
        : undefined;

      await this.fetchNextAuditLogs({
        organization_id: this.organizationId,
        from_date: fromTimestamp,
        to_date: toTimestamp,
        log_action: this.selectedAction || undefined,
      });
    },
    setDefaultDates() {
      const now = new Date();

      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
        2,
        "0"
      )}-${String(now.getDate()).padStart(2, "0")}`;

      const firstDay = `${now.getFullYear()}-${String(
        now.getMonth() + 1
      ).padStart(2, "0")}-01`;

      this.fromDate = firstDay;
      this.toDate = today;

      const [fy, fm, fd] = firstDay.split("-");
      this.fromDateDisplay = `${fm}/${fd}/${fy}`;

      const [ty, tm, td] = today.split("-");
      this.toDateDisplay = `${tm}/${td}/${ty}`;

      this.tempFromDate = new Date(now.getFullYear(), now.getMonth(), 1);
      this.tempToDate = now;
    },

    // 🔥 TIMESTAMP FIX
    async loadAuditLogs() {
      this.loading = true;
      try {
        const fromTimestamp = this.fromDate
          ? new Date(this.fromDate + "T00:00:00").getTime()
          : undefined;

        const toTimestamp = this.toDate
          ? new Date(this.toDate + "T23:59:59").getTime()
          : undefined;

        await this.fetchAuditLogs({
          organization_id: this.organizationId, // ✅ unchanged
          from_date: fromTimestamp,
          to_date: toTimestamp,
          log_action: this.selectedAction || undefined,
        });
      } finally {
        this.loading = false;
      }
    },

    formatDate(ts) {
      if (!ts) return "-";
      const d = new Date(Number(ts));

      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      const yyyy = d.getFullYear();

      return `${mm}/${dd}/${yyyy}`;
    },

    formatTime(ts) {
      if (!ts) return "-";
      const d = new Date(Number(ts));

      let hours = d.getHours();
      const minutes = String(d.getMinutes()).padStart(2, "0");

      const ampm = hours >= 12 ? "PM" : "AM";
      hours = hours % 12 || 12;

      return `${hours}:${minutes} ${ampm}`;
    },

    formatDateInput(e, key) {
      let v = e.target.value.replace(/\D/g, "").slice(0, 8);
      if (v.length > 2) v = v.slice(0, 2) + "/" + v.slice(2);
      if (v.length > 4) v = v.slice(0, 5) + "/" + v.slice(5);
      this[key] = v;
    },

    validateAndFormatDate(displayKey, actualKey) {
      const value = this[displayKey];
      if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return;

      const [m, d, y] = value.split("/").map(Number);
      this[actualKey] = `${y}-${String(m).padStart(2, "0")}-${String(
        d
      ).padStart(2, "0")}`;
    },

    setDateFromPicker(val, displayKey, actualKey, pickerKey) {
      const d = new Date(val);
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      const day = d.getDate();

      this[displayKey] = `${String(m).padStart(2, "0")}/${String(day).padStart(
        2,
        "0"
      )}/${y}`;
      this[actualKey] = `${y}-${String(m).padStart(2, "0")}-${String(
        day
      ).padStart(2, "0")}`;
      this[pickerKey] = false;
    },
  },
};
</script>